/**
 * AIConsentPolicyDialog — full-screen overlay that shows the Unified
 * Privacy Policy on top of the AI Consent gate WITHOUT unmounting it.
 *
 * Why an overlay instead of a route:
 *   Navigating to /account/agreement (or any route) unmounts the gate,
 *   loses the checkbox state, and on iOS Capacitor briefly renders a
 *   white splash. Stacking a portal on top of the gate keeps everything
 *   in-memory and returns the user to the exact same modal state when
 *   they close it — matches how Apple's own iOS system dialogs behave.
 *
 * z-index sits at 140 (gate is 130) so it always covers the gate.
 */
import { createPortal } from "react-dom";
import { CaretRight, ShieldCheck } from "@phosphor-icons/react";
import AIConsentPolicyContent from "./AIConsentPolicyContent";

const NAVY = "#0A192F";
const GOLD = "#D4AF37";
const GOLD_DARK = "#A88425";

export default function AIConsentPolicyDialog({ open, onClose }) {
  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[140] bg-[#0A192F]/70 backdrop-blur-md flex items-center justify-center font-tajawal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-policy-title"
      data-testid="ai-consent-policy-dialog"
      dir="rtl"
      style={{
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
        paddingLeft: "12px",
        paddingRight: "12px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl relative flex flex-col animate-policy-in"
        style={{
          background: "#FFFFFF",
          borderRadius: "22px",
          boxShadow: "0 32px 80px -20px rgba(10,25,47,0.6)",
          border: "1px solid rgba(212,175,55,0.35)",
          maxHeight: "calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 24px)",
        }}
      >
        {/* Header — gold band with back button */}
        <header
          className="px-4 py-3 shrink-0 flex items-center gap-3 rounded-t-[22px]"
          style={{
            background: `linear-gradient(180deg, #FFF8E1 0%, #FFFFFF 100%)`,
            borderBottom: `1px solid ${GOLD}33`,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="رجوع لشاشة الموافقة"
            data-testid="ai-consent-policy-back"
            className="w-9 h-9 rounded-full bg-white shadow-sm hover:shadow-md flex items-center justify-center transition active:scale-95"
            style={{ border: `1px solid ${GOLD}55` }}
          >
            <CaretRight size={16} weight="bold" color={NAVY} />
          </button>

          <div className="flex-1 min-w-0 text-right">
            <div className="flex items-center justify-end gap-1.5 mb-0.5">
              <ShieldCheck size={13} weight="fill" color={GOLD_DARK} />
              <h2
                id="ai-policy-title"
                className="text-[14px] font-extrabold"
                style={{ color: NAVY }}
              >
                سياسة الذكاء الاصطناعي
              </h2>
            </div>
            <p className="text-[10.5px] font-bold" style={{ color: GOLD_DARK }}>
              مُخَصَّصة لِلمِيزات الذَّكيّة فَقَط
            </p>
          </div>
        </header>

        {/* Scrollable content — reuses the single-source-of-truth doc */}
        <div
          className="px-4 sm:px-5 py-4 overflow-y-auto text-right"
          data-testid="ai-consent-policy-scroll"
          style={{ flex: 1, background: "#FAFAFA" }}
        >
          <div
            className="bg-white rounded-2xl px-3 sm:px-5 py-4 border border-[#EFEFEF]"
            style={{ boxShadow: "0 2px 12px rgba(10,25,47,0.04)" }}
          >
            <AIConsentPolicyContent />
          </div>
        </div>

        {/* Sticky footer — return CTA */}
        <footer
          className="px-4 py-3 shrink-0 rounded-b-[22px]"
          style={{
            borderTop: "1px solid #EFEFEF",
            background: "#FFFFFF",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            data-testid="ai-consent-policy-close"
            className="w-full h-11 rounded-2xl font-extrabold text-[13.5px] text-white transition active:scale-[0.98]"
            style={{
              background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`,
              boxShadow: `0 6px 16px -4px ${GOLD}88`,
            }}
          >
            رُجوع لِشاشة المُوافَقة
          </button>
        </footer>

        <style>{`
          @keyframes policy-in {
            from { transform: translateY(24px) scale(0.98); opacity: 0; }
            to   { transform: translateY(0)    scale(1);    opacity: 1; }
          }
          .animate-policy-in { animation: policy-in 240ms cubic-bezier(0.22,1,0.36,1); }
        `}</style>
      </div>
    </div>,
    document.body,
  );
}
